import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useHardWord from "src/hook/components/useHardWord";
import { useGeneralSettings } from "src/hook/data/useGeneralSettings";
import { useLearn } from "src/hook/data/useLearn";
import { useLearning } from "src/hook/data/useLearning";
import useProgress from "src/hook/data/useProgress";
import usePopUp from "src/hook/modal/usePopUp";
import { Actions, LearningArray, PageSource } from "src/model/enum/enums";
import { Word } from "src/model/Word";
import XIcon from "../XIcon/XIcon";
import styles from "./HardWordBanner.module.scss";

interface HardWordBannerProps {
    word: Word;
    pageSource: PageSource;
}

const HardWordBanner: React.FC<HardWordBannerProps> = ({ word, pageSource }) => {
    const { isTooHard } = useHardWord();
    const { saveProgress } = useProgress();
    const { removeWord } = useLearning();
    const { generalSettings, updateGeneralSettings } = useGeneralSettings();
    const { show, hide } = usePopUp();
    const [isVisible, setIsVisible] = useState(true);
    const [closeCount, setCloseCount] = useState(generalSettings.HardWordBannerCloseCount || 0);
    const { completeCurrentCard } = useLearn();
    const { t } = useTranslation();



    const handleClose = () => {
        const newCloseCount = closeCount + 1;
        setCloseCount(newCloseCount);

        if (newCloseCount >= 3) {
            show({
                title: 'Hard Word Banner',
                message: 'You don\'t want to see this banner?',
                primaryButtonText: 'Don\'t Show',
                primaryButtonClick: handleDontShow,
                showSecondaryButton: true,
                secondaryButtonText: 'Keep Showing',
                secondaryButtonClick: handleKeepShowing,
            });
        } else {
            setIsVisible(false);
            updateGeneralSettings({ HardWordBannerCloseCount: newCloseCount });
        }
    }

    const handleDontShow = () => {
        hide();
        updateGeneralSettings({
            ShowHardWordBanner: false,
            HardWordBannerCloseCount: 0
        });
    }

    const handleKeepShowing = () => {
        hide();
        setCloseCount(0);
        setIsVisible(true);
    }

    const handleAccept = () => {
        if (pageSource === PageSource.Challenge) {
            removeWord(LearningArray.ReviewWordIds, word?.ID!);
            saveProgress({
                Action: Actions.ScheduleReview,
                Value: `${word.ID}-${100}`
            });
        }
        else {
            completeCurrentCard();
        }
    }

    if (!isTooHard(word) || !generalSettings.ShowHardWordBanner || !isVisible) return null;

    return (
        <div className={styles.hardWordBanner}>
            <div className={styles.info}>
                <div className={styles.wordNumber}>{`Word #${word?.Rank.toLocaleString()}`}</div>
                <div className={styles.message}>{t('Outside your range. Learn later?')}</div>
            </div>
            <button className={styles.accept} onClick={handleAccept}>{t('Accept')}</button>
            <XIcon onClick={handleClose} className='XsIcon' forceLeft={true} />
        </div>
    );
};

export default HardWordBanner;
